import type { LeadState } from '@/api/types/crm/crm.types'

/** Значение `state` из `crm-lead-funnel/card-funnel` → вкладка/кнопки по связке. */
export function parseLeadStateFromCardFunnelState(
  raw: unknown,
): LeadState | undefined {
  if (typeof raw !== 'string' || !raw.trim()) return undefined
  const s = raw.toLowerCase().trim()
  if (s === 'new' || s === 'in_work' || s === 'processed' || s === 'client') {
    return s
  }
  return undefined
}

/**
 * Состояние заявки по выбранной воронке (снимок card-funnel), не глобальный `lead.type`.
 * Если воронка выбрана, но снимок ещё тянется — `undefined` (кнопки переходов не показываем).
 */
export function resolveLeadWorkflowState(params: {
  selectedFunnelId: number | null
  getCardInFunnelSnapshot: (
    funnelId: number,
    leadFunnelId?: number | null,
  ) => { state?: unknown } | undefined
  isRefetchingLeadCard: boolean
  globalType: LeadState | undefined
}): LeadState | undefined {
  const {
    selectedFunnelId,
    getCardInFunnelSnapshot,
    isRefetchingLeadCard,
    globalType,
  } = params

  if (
    selectedFunnelId !== null &&
    Number.isFinite(Number(selectedFunnelId)) &&
    Number(selectedFunnelId) > 0
  ) {
    const fid = Number(selectedFunnelId)
    const fromSnap = parseLeadStateFromCardFunnelState(
      getCardInFunnelSnapshot(fid)?.state,
    )
    if (fromSnap !== undefined) return fromSnap
    if (isRefetchingLeadCard) return undefined
    return globalType
  }

  return globalType
}

type LeadLikeWithFunnels = {
  lead_funnel_id?: number
  funnels?: Array<{
    id?: number | string | null
    lead_funnel_id?: number | string | null
  }>
}

export function getShoppingCurrentLeadFunnelId(params: {
  lead: LeadLikeWithFunnels
  selectedFunnelId: number | null
}): number | undefined {
  const { lead, selectedFunnelId } = params

  const funnels = Array.isArray(lead?.funnels)
    ? (lead.funnels as Array<{
        id?: number | string | null
        lead_funnel_id?: number | string | null
      }>)
    : []

  // Если у лида вообще нет воронок — нельзя делать funnel-scoped операции
  if (funnels.length === 0) return undefined

  if (selectedFunnelId === null) {
    const fallback = Number(lead?.lead_funnel_id)
    const isFallbackValid =
      Number.isFinite(fallback) &&
      fallback > 0 &&
      funnels.some((f) => Number(f?.lead_funnel_id) === fallback)

    return isFallbackValid ? fallback : undefined
  }

  const currentFunnel = funnels.find(
    (funnel) => Number(funnel?.id) === selectedFunnelId,
  )
  const currentLeadFunnelId = Number(currentFunnel?.lead_funnel_id)

  return Number.isFinite(currentLeadFunnelId) && currentLeadFunnelId > 0
    ? currentLeadFunnelId
    : undefined
}

